import React from 'react'
import { Box, Button, Field, Flex, Message, Text } from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { parseErrorMessage } from 'api'
import { CompumaticPunch } from 'api/admin/timeClock/listCompumaticPunches'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'
import { useEditCompumaticPunchMutation } from 'queries/admin/timeClock/useEditCompumaticPunchMutation'
import {
  COMPUMATIC_PUNCH_TYPE_OPTIONS,
  isKnownCompumaticPunchType,
} from '../../../formatCompumaticLabels'
import { useCompumaticClockPunchesWorkflowsContextValue } from '../../../context/CompumaticClockPunchesWorkflowsProvider'
import { editCompumaticPunchSchema, EditCompumaticPunchSchema } from '../schema'
import {
  CompumaticCompanyLocationCell,
  CompumaticPunchCell,
  CompumaticShiftCell,
  CompumaticWorkerCell,
} from '../../../components/CompumaticPunchCells'

const punchTypeOptions = COMPUMATIC_PUNCH_TYPE_OPTIONS.map((option) => ({
  label: option.label,
  value: option.value,
}))

export const EditCompumaticPunchFlowMain = ({
  punch,
}: {
  punch: CompumaticPunch
}) => {
  const {
    editPunchFlow: { isOpen, close },
  } = useCompumaticClockPunchesWorkflowsContextValue()

  const {
    mutate,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useEditCompumaticPunchMutation()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditCompumaticPunchSchema>({
    resolver: zodResolver(editCompumaticPunchSchema),
    values: {
      punchType: isKnownCompumaticPunchType(punch.punchType)
        ? punch.punchType
        : 'clockIn',
    },
  })

  const errorMessage = parseErrorMessage(error)

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    mutate(
      {
        punchId: punch.id,
        punchType: data.punchType,
        punchAt: punch.punchAt,
      },
      {
        onSuccess: () => {
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect open={isOpen} onClose={handleClose} position="top">
      <Text fontWeight={600} mb={3}>
        Edit punch
      </Text>
      <Flex flexDirection="column" gap={2} mb={3}>
        <Box>
          <Text fontSize={0} color="lightText" mb={1}>
            Worker
          </Text>
          <CompumaticWorkerCell punch={punch} />
        </Box>
        <Box>
          <Text fontSize={0} color="lightText" mb={1}>
            Company / Location
          </Text>
          <CompumaticCompanyLocationCell punch={punch} />
        </Box>
        <Box>
          <Text fontSize={0} color="lightText" mb={1}>
            Punch
          </Text>
          <CompumaticPunchCell punch={punch} />
        </Box>
        <Box>
          <Text fontSize={0} color="lightText" mb={1}>
            Shift
          </Text>
          <CompumaticShiftCell punch={punch} />
        </Box>
      </Flex>
      <Box as="form" onSubmit={onSubmit}>
        <Field label="Punch type" error={errors.punchType?.message}>
          <Controller
            control={control}
            name="punchType"
            render={({ field }) => (
              <ModalSelect
                block
                options={punchTypeOptions}
                value={
                  punchTypeOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(option) => {
                  field.onChange(option?.value ?? '')
                }}
              />
            )}
          />
        </Field>

        {isError && errorMessage ? (
          <Message variant="error" mt={3}>
            {errorMessage}
          </Message>
        ) : null}

        <Flex gap={2} mt={4}>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? 'Saving…' : 'Save and Reprocess'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={isPending}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Flex>
      </Box>
    </ModalWithSelect>
  )
}
